/**
 * Inbox composer: multiline input, send vs schedule, and delayed-send countdown shared with parent.
 *
 * Where: bottom of `ConversationView`.
 * Uses: MUI + `DateTimePicker` for scheduling.
 * Behavior: global pending-send (see `InboxPendingSendContext`) disables this composer when it targets `activeChatId`; optional schedule menu; disables while `isSending`.
 */
import React, { useState, useCallback } from 'react';
import {
  Box,
  IconButton,
  CircularProgress,
  TextareaAutosize,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  KeyboardArrowDown as ArrowDownIcon,
  CalendarMonth as CalendarIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
interface MessageInputProps {
  /** True when a delayed send is queued for this conversation (input disabled). */
  composerBlockedByPendingSend: boolean;
  onQueueSend: (message: string, chatId?: string) => void;
  onScheduleMessage?: (message: string, scheduledAt: Date) => void;
  isSending: boolean;
  disabled?: boolean;
  activeChatId?: string;
  scheduledCount?: number;
  failedScheduledCount?: number;
  onCalendarClick?: () => void;
  isScheduledView?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  composerBlockedByPendingSend,
  onQueueSend,
  onScheduleMessage,
  isSending,
  disabled = false,
  activeChatId,
  scheduledCount = 0,
  failedScheduledCount = 0,
  onCalendarClick,
  isScheduledView = false,
}) => {
  const [message, setMessage] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30);
    return d;
  });

  const handleSend = useCallback(() => {
    const trimmedMessage = message.trim();
    if (
      trimmedMessage &&
      !isSending &&
      !disabled &&
      !composerBlockedByPendingSend &&
      activeChatId
    ) {
      setMessage('');
      onQueueSend(trimmedMessage, activeChatId);
    }
  }, [message, isSending, disabled, composerBlockedByPendingSend, activeChatId, onQueueSend]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleScheduleClick = useCallback(() => {
    handleMenuClose();
    if (message.trim()) {
      setScheduleModalOpen(true);
    }
  }, [handleMenuClose, message]);

  const handleScheduleModalClose = useCallback(() => {
    setScheduleModalOpen(false);
  }, []);

  const handleScheduleConfirm = useCallback(() => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && onScheduleMessage && scheduledDate) {
      const date = scheduledDate instanceof Date ? scheduledDate : new Date(String(scheduledDate));
      onScheduleMessage(trimmedMessage, date);
      setMessage('');
      setScheduleModalOpen(false);
    }
  }, [message, onScheduleMessage, scheduledDate]);

  const isInputDisabled = isSending || disabled || composerBlockedByPendingSend;
  const isSendDisabled =
    !message.trim() || isSending || disabled || composerBlockedByPendingSend;

  return (
    <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextareaAutosize
            minRows={1}
            maxRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isSending ? 'Sending message...' : 'Type your message here'}
            disabled={isInputDisabled}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'inherit',
              resize: 'none',
              outline: 'none',
              minWidth: 0,
              width: '100%',
              opacity: isInputDisabled ? 0.7 : 1,
              cursor: isInputDisabled ? 'not-allowed' : 'text',
            }}
          />
          {((scheduledCount > 0 || failedScheduledCount > 0) || isScheduledView) && onCalendarClick && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {failedScheduledCount > 0 && (
                <Tooltip title={`${failedScheduledCount} failed message(s) - view in scheduled messages`}>
                  <EventBusyIcon
                    fontSize="small"
                    sx={{ color: 'error.main', mr: 0.5 }}
                  />
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={onCalendarClick}
                disabled={disabled}
                color={isScheduledView ? 'primary' : 'default'}
                sx={{ p: 0.5 }}
                title={isScheduledView ? 'Back to conversation' : 'View scheduled messages'}
              >
                <CalendarIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSend}
              disabled={isSendDisabled}
            >
              {isSending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              disabled={disabled}
              sx={{ px: 0, ml: -1 }}
            >
              <ArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MenuItem
          onClick={handleScheduleClick}
          disabled={!message.trim()}
        >
          Schedule message
        </MenuItem>
      </Menu>

      <Dialog open={scheduleModalOpen} onClose={handleScheduleModalClose} maxWidth="xs" fullWidth>
        <DialogTitle>Send this message on...</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Date and time"
              value={scheduledDate}
              onChange={(v) => setScheduledDate(v)}
              minDateTime={new Date()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mt: 1 },
                },
              }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleScheduleModalClose}>Cancel</Button>
          <Button variant="contained" onClick={handleScheduleConfirm}>
            Schedule
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
